/**
 * ProcessingOptionsForm - Processing options configuration
 * @module components/starnexus/wizard/forms/ProcessingOptionsForm
 *
 */

import React from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Slider from '@amzn/awsui-components-console/slider';
import Input from '@amzn/awsui-components-console/input';
import Checkbox from '@amzn/awsui-components-console/checkbox';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import type { ProcessingOptions } from '../../../../types/starnexus';

interface ProcessingOptionsFormProps {
  options: ProcessingOptions;
  onChange: (options: ProcessingOptions) => void;
}

export const ProcessingOptionsForm: React.FC<ProcessingOptionsFormProps> = ({
  options,
  onChange
}) => {
  const handleConcurrencyChange = (value: number) => {
    onChange({ ...options, concurrencyLimit: value });
  };

  const handleRetryChange = (
    field: 'maxAttempts' | 'backoffMultiplier',
    value: number
  ) => {
    onChange({
      ...options,
      retryPolicy: { ...options.retryPolicy, [field]: value }
    });
  };

  const handleValidationChange = (
    field: keyof typeof options.validation,
    checked: boolean
  ) => {
    onChange({
      ...options,
      validation: { ...options.validation, [field]: checked }
    });
  };

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h3">Concurrency</Header>}>
        <FormField
          label="Concurrency limit"
          description="Maximum number of files to process simultaneously"
        >
          <SpaceBetween size="m" direction="horizontal" alignItems="center">
            <Slider
              value={options.concurrencyLimit}
              onChange={({ detail }) => handleConcurrencyChange(detail.value)}
              min={1}
              max={20}
            />
            <Input
              value={String(options.concurrencyLimit)}
              onChange={({ detail }) =>
                handleConcurrencyChange(
                  Math.min(20, Math.max(1, parseInt(detail.value) || 1))
                )
              }
              type="number"
              inputMode="numeric"
              controlId="concurrency-input"
            />
          </SpaceBetween>
        </FormField>
      </Container>

      <Container header={<Header variant="h3">Retry Policy</Header>}>
        <ColumnLayout columns={2}>
          <FormField
            label="Max retry attempts"
            description="Number of times to retry failed processing"
          >
            <Input
              value={String(options.retryPolicy.maxAttempts)}
              onChange={({ detail }) =>
                handleRetryChange(
                  'maxAttempts',
                  Math.max(1, parseInt(detail.value) || 1)
                )
              }
              type="number"
              inputMode="numeric"
            />
          </FormField>

          <FormField
            label="Backoff multiplier"
            description="Multiplier for exponential backoff between retries"
          >
            <Input
              value={String(options.retryPolicy.backoffMultiplier)}
              onChange={({ detail }) =>
                handleRetryChange(
                  'backoffMultiplier',
                  Math.max(1, parseFloat(detail.value) || 1)
                )
              }
              type="number"
              inputMode="decimal"
            />
          </FormField>
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h3">Validation Options</Header>}>
        <SpaceBetween size="s">
          <Checkbox
            checked={options.validation.checkSchema}
            onChange={({ detail }) =>
              handleValidationChange('checkSchema', detail.checked)
            }
          >
            Validate schema
            <Box variant="small" color="text-body-secondary">
              Verify incoming data matches the expected schema
            </Box>
          </Checkbox>

          <Checkbox
            checked={options.validation.checkSize}
            onChange={({ detail }) =>
              handleValidationChange('checkSize', detail.checked)
            }
          >
            Check file size
            <Box variant="small" color="text-body-secondary">
              Reject files that exceed size limits
            </Box>
          </Checkbox>

          <Checkbox
            checked={options.validation.checkDuplicates}
            onChange={({ detail }) =>
              handleValidationChange('checkDuplicates', detail.checked)
            }
          >
            Check for duplicates
            <Box variant="small" color="text-body-secondary">
              Detect and handle duplicate records
            </Box>
          </Checkbox>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default ProcessingOptionsForm;
